import { useLiveQuery } from 'dexie-react-hooks'
import { Link } from 'react-router'
import type { MessageContext } from '../../api/types'
import { Icon } from '../../components/Icon'
import { db } from '../../db/db'
import { channelDisplayName } from '../../lib/channels'
import { useConversation } from './ConversationContext'
import { replySummary } from './ReplyQuote'

export interface ContextPreview {
  /** "Replying to #Company notices" */
  title: string
  /** The notice's first lines, or why they can't be shown. */
  text: string
  /** The notice's seq when it is stored on this device, to jump to it. */
  seq: number | undefined
}

/** What a private reply answers, from this device's copy of the channel and message. */
export function useContextPreview(context: MessageContext): ContextPreview {
  const { users, me } = useConversation()
  const found = useLiveQuery(
    async () => {
      const [channel, message] = await Promise.all([db.channels.get(context.channelId), db.messages.get(context.messageId)])
      return { channel: channel ?? null, message: message ?? null }
    },
    [context.channelId, context.messageId],
  )
  const title = found?.channel ? `Replying to #${channelDisplayName(found.channel, users, me.id)}` : 'Replying to a channel message'
  let text = ''
  if (found) {
    text = found.message ? replySummary(found.message) : 'Message not available on this device'
  }
  return { title, text, seq: found?.message?.seq }
}

/** Above a private reply in a bot conversation: the channel message it answers, linking to it. */
export function ContextQuote({ context }: { context: MessageContext }) {
  const { title, text, seq } = useContextPreview(context)
  const to = `/c/${encodeURIComponent(context.channelId)}?m=${encodeURIComponent(context.messageId)}${seq ? `&seq=${seq}` : ''}`
  return (
    <Link to={to} className="reply-quote context-quote" aria-label={`${title}: ${text || 'the original message'}. Open the message`}>
      <strong>
        <Icon name="megaphone" />
        {title}
      </strong>
      <span>{text}</span>
    </Link>
  )
}
